import { Fragment, useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  browserTimeZone,
  useDraftActions,
  useOutreachHeldEvents,
  useOutreachOperation,
  useOutreachSettings,
  useOutreachSettingsMutations,
  useOutreachTemplateMutations,
  useOutreachTemplates,
  useStartFirstTouchDrafts,
} from '@discovr/features';
import type { OutreachApproveSendOptions, OutreachDraft, OutreachOperation, OutreachSettings, OutreachSettingsInput } from '@discovr/types';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Check, PaperPlaneTilt } from '../../../../design-system/icons';
import { cn } from '../../../../lib/utils';
import type { DraftCardEdit } from '../DraftCard';
import { draftPlaceholder, draftSaveError, isIncompleteDraft, isOverBodyCap, isOverSubjectCap, placeholderCopy, type DraftSaveError } from '../draftLabels';
import { failedTotal } from '../operationOutcomes';
import { WIZARD_BATCH_EXPIRED_COPY } from '../errorCopyByContext';
import { codeOf, detailsOf, errorCopy } from '../sendLabels';
import { approveBatchExpired, summarizeApprove } from './approveSummary';
import type { AwaySend } from './AwaySendWatchers';
import { BulkSendConfirmation, type ConfirmationTab } from './BulkSendConfirmation';
import { toastSendFinished } from './sendFinishedToast';
import { RESULT_DESC_ID, RESULT_TITLE_ID, SendingView, STILL_SENDING_AFTER_MS } from './SendingView';
import type { ConfirmationRecipient, SummaryPerson } from './sendSummaryParts';
import { batchChangeLine, SCHEDULE_PAST_COPY, SCHEDULE_TOO_FAR_COPY, scheduledTimeLabel } from './bulkSendCopy';
import type { WizardRun, WizardSessionStore } from '../useSendWizardSession';
import { commitBatchTemplate, resolveTemplate } from './batchTemplate';
import { CreatorsStep } from './CreatorsStep';
import { DraftMenu } from './DraftMenu';
import { DraftsStep } from './DraftsStep';
import { RiskyDrafts } from './RiskyDrafts';
import { SendStep } from './SendStep';
import { TemplateStep } from './TemplateStep';
import { useBatchDrafts, type BatchDraftItem } from './useBatchDrafts';
import { useLiveText } from './useLiveText';
import { canContinue, effectiveRecipient, initialWizardState, isBeyondScheduleCap, isFutureSchedule, isZoneChange, scheduledForISO, suppressedDropped, sendableTargets, usableBatchId, wizardReducer, WIZARD_STEPS, type WizardStep, type WizardTarget } from './wizardState';

export interface BulkSendWizardProps {
  open: boolean;
  campaignId: string;
  targets: WizardTarget[];
  /**
   * Owned by the parent (not local state) so a still-running or just-finished
   * operation survives this component unmounting/remounting — e.g. the parent
   * re-rendering a different sub-view and back. See onOperationIdChange/onSummaryChange.
   * This is the `draft_initial` operation.
   */
  operationId: string | null;
  /** Set once the approve operation finishes. */
  summary: string | null;
  /** The `approve_drafts` operation, lifted for the same reason as operationId. */
  approveOperationId: string | null;
  onOperationIdChange: (operationId: string | null) => void;
  onApproveOperationIdChange: (operationId: string | null) => void;
  onSummaryChange: (summary: string | null) => void;
  onClose: () => void;
  /** Closing while drafts are still being written puts the panel away and keeps the session (the header badge reopens it). */
  onHide?: () => void;
  onQueued: (result: { messageId?: number; operationId?: string }) => void;
  /** The admin closed the wizard while the approve was still running: the owner keeps watching it and toasts its end. */
  onLeaveSending?: (send: AwaySend) => void;
  /** Owned by the parent like operationId, so a remount resumes on the same step, schedule, batch and recipients. Local when omitted. */
  session?: WizardSessionStore;
  /** P32 Q8: the confirmation's Go to Inbox (Contacted tab) / View scheduled — closes the wizard on that inbox tab. */
  onNavigate?: (tab: ConfirmationTab) => void;
  /** Send anyway on a risky address is admin tier only (outreach:manage, owner ruling 2026-10-12). */
  canOverrideAddressGuard?: boolean;
  /** Without write access (outreach:write at campaign level) the wizard only shows the drafts: no Retry, edit or Approve. Defaults to true. */
  canWrite?: boolean;
}

// Only for display and scheduling math before settings arrive; never offered as a pick.
const FALLBACK_DISPLAY_ZONE = 'Asia/Kolkata';

const stepLabels: Record<WizardStep, string> = { creators: 'Creators', template: 'Template', drafts: 'Drafts', send: 'Send' };

function toSettingsInput(settings: OutreachSettings): OutreachSettingsInput {
  const { campaignId: _campaignId, exists: _exists, pausedAt: _pausedAt, updatedAt: _updatedAt, templateLocked: _templateLocked, region: _region, regionSet: _regionSet, ...input } = settings;
  return input;
}

function templateSaveError(e: unknown): string {
  return `Couldn't save the template, so nothing was drafted. ${errorCopy(codeOf(e), detailsOf(e), 'template')}`;
}

function draftStartError(e: unknown): string {
  return `Couldn't start the drafts. ${errorCopy(codeOf(e), detailsOf(e))}`;
}

function zoneSaveError(e: unknown): string {
  return `Couldn't update the campaign's time zone, so nothing was sent. ${errorCopy(codeOf(e), detailsOf(e))}`;
}

/** The zone PUT is not undone when the approve after it fails, so the error says so. */
function approveError(e: unknown, zoneSaved: boolean): string {
  const copy = errorCopy(codeOf(e), detailsOf(e));
  return zoneSaved ? `The campaign's time zone was updated, but these drafts were not approved. ${copy}` : copy; // copy: ours
}

export function BulkSendWizard({ open, campaignId, targets, operationId, summary, approveOperationId, onOperationIdChange, onApproveOperationIdChange, onSummaryChange, onClose, onHide, onQueued, onLeaveSending, onNavigate, session, canOverrideAddressGuard = false, canWrite = true }: BulkSendWizardProps) {
  const settingsQuery = useOutreachSettings(campaignId);
  const { save } = useOutreachSettingsMutations(campaignId);
  const templatesQuery = useOutreachTemplates(campaignId);
  const templateMutations = useOutreachTemplateMutations(campaignId);
  const startDrafts = useStartFirstTouchDrafts(campaignId);
  const draftActions = useDraftActions(campaignId);
  // Undefined while settings load: the Send step's zone control waits for it.
  const loadedCampaignZone = settingsQuery.data?.sendTz;
  const campaignZone = loadedCampaignZone ?? FALLBACK_DISPLAY_ZONE;
  // Z1: Send now runs in the admin's own zone (the browser's); Schedule runs in the campaign zone.
  const adminZone = useMemo(() => browserTimeZone(), []);
  // U2: a creator's own zone is reference-only; the schedule starts in the campaign zone.
  // A remount resumes the saved state, with today's targets (an address edited meanwhile must count).
  const [state, dispatch] = useReducer(wizardReducer, { targets, templateId: settingsQuery.data?.sequenceTemplateId ?? null }, (initial) => {
    const saved = session?.readSaved();
    return saved ? { ...saved, targets: initial.targets } : initialWizardState(initial.targets, { templateId: initial.templateId, step: operationId ? 'drafts' : 'creators' });
  });
  const [error, setError] = useState<string | null>(null);
  // A picked time drifts into the past while the step sits open: re-render so Schedule disables itself.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (state.step !== 'send' || state.when !== 'later') return undefined;
    const id = setInterval(() => setClockTick((tick) => tick + 1), 15_000);
    return () => clearInterval(id);
  }, [state.step, state.when]);
  // P32 Q4: the creators whose drafts are ready when Drafts' Continue is pressed are this batch; later ones wait
  // for another send. The batch holds creators, not draft ids: a draft regenerated while the wizard is open is a
  // new message, and it must stay in this send (o-e2e2 N4).
  const [localBatchEntryIds, setLocalBatchEntryIds] = useState<string[] | null>(null);
  const batchEntryIds = session ? session.batchEntryIds : localBatchEntryIds;
  const setBatchEntryIds = session ? session.setBatchEntryIds : setLocalBatchEntryIds;
  const [localRun, setLocalRun] = useState<WizardRun | null>(null);
  const run = session ? session.run : localRun;
  const setRun = session ? session.setRun : setLocalRun;
  const [cardErrors, setCardErrors] = useState<Record<number, DraftSaveError | undefined>>({});
  const [editedIds, setEditedIds] = useState<number[]>([]);
  const [savingIds, setSavingIds] = useState<number[]>([]);
  // The Drafts step reports a mid-edit card up: Continue lives here, not in the steps themselves (Finding 3).
  const [messageEditingBlocked, setMessageEditingBlocked] = useState(false);
  const [localRecipients, setLocalRecipients] = useState<Record<number, ConfirmationRecipient>>({});
  const approvedRecipients = session ? session.recipients : localRecipients;
  const setApprovedRecipients = session ? session.setRecipients : setLocalRecipients;
  // Refs, not state: a second click can land before the first one's re-render.
  const approvingRef = useRef(false);
  const committingRef = useRef(false);
  const [approving, setApproving] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const zoneSync = useRef<{ zone: string; promise: Promise<boolean> } | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const [pickedIds, setPickedIds] = useState<number[]>([]);
  const batch = useBatchDrafts(campaignId, operationId, sendableTargets(state), run?.batch, pickedIds);
  const approveProgress = useOutreachOperation(campaignId, approveOperationId);
  // Collected from the approve on, not from the confirmation's mount: a cap
  // deferral can land while the approve operation is still running.
  const heldEvents = useOutreachHeldEvents(campaignId, approveOperationId !== null);
  // A poll error must not trap the admin behind a disabled Done — the approve already happened server-side.
  const approveProgressError = approveOperationId !== null && !!approveProgress.isError;
  // A still-running or just-finished operation (operationId set) means this is
  // the SAME wizard session being resumed after a remount — don't clear the form error over it.
  const resumingOperation = operationId !== null;

  useEffect(() => {
    if (!open || resumingOperation) return;
    // A genuinely new wizard session (not a resumed one) resets its local
    // form error once, on the open transition — bounded by the early return above.
    setError(null);
  }, [open, resumingOperation]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // The campaign's saved pick is where the batch starts; once settings arrive an untouched (null) pick adopts it.
  const savedPickId = settingsQuery.data?.sequenceTemplateId ?? null;
  useEffect(() => {
    if (state.templateId === null && savedPickId !== null) dispatch({ type: 'template', templateId: savedPickId });
  }, [state.templateId, savedPickId]);
  const template = resolveTemplate(templatesQuery.data, state.templateId);

  const items: BatchDraftItem[] = batch.items;
  const readyIds = items.flatMap((item) => (item.state === 'ready' ? [item.draft.message.id] : []));
  const hasIncompleteDraft = items.some((item) => item.state === 'ready' && (isIncompleteDraft(item.draft) || isOverBodyCap(item.draft) || isOverSubjectCap(item.draft) || !!draftPlaceholder(item.draft)));
  const readyEntryIds = items.flatMap((item) => (item.state === 'ready' ? [item.entryId] : []));
  const inBatch = (entryId: string) => !batchEntryIds || batchEntryIds.includes(entryId);
  // P32 Q3: the send step sends every draft in the batch.
  const approveIds = items.flatMap((item) => (item.state === 'ready' && inBatch(item.entryId) ? [item.draft.message.id] : []));
  // A batch draft being rewritten (regenerated behind the open wizard) rejoins once ready; the send step says so
  // meanwhile, and says so too when that rewrite failed.
  const batchStates = batchEntryIds ? items.filter((item) => batchEntryIds.includes(item.entryId)).map((item) => item.state) : [];
  const rewritingCount = batchStates.filter((st) => st === 'writing').length;
  const droppedCount = batchStates.filter((st) => st === 'failed' || st === 'skipped').length;
  const batchItems = items.filter((item): item is Extract<BatchDraftItem, { state: 'ready' }> => item.state === 'ready' && approveIds.includes(item.draft.message.id));
  // A draft with no effective address is approved all the same: Go parks it until an email arrives (owner 2026-10-15).
  const waitingIds = batchItems.filter((item) => !effectiveRecipient(item.draft)).map((item) => item.draft.message.id);
  const sendIds = approveIds;
  const liveBatchChange = useLiveText(batchChangeLine(rewritingCount, droppedCount) ?? '');

  const recipientsFor = (ids: number[]): Record<number, ConfirmationRecipient> => {
    const wanted = new Set(ids);
    return Object.fromEntries(batchItems.filter((item) => wanted.has(item.draft.message.id)).map((item) => [item.draft.message.id, { username: item.username, displayName: item.draft.entry.displayName, avatarUrl: item.draft.entry.avatarUrl, ...(effectiveRecipient(item.draft) ? {} : { waiting: true }) }]));
  };

  // Ruling 37: every approved draft takes the batch's schedule and mailbox; Send now's
  // flag is added at the approveMany call, never with a schedule.
  const sendChoices = (): OutreachApproveSendOptions => ({
    scheduledFor: scheduledForISO(state, campaignZone),
  });

  // U1: a zone chosen on the Send step is the campaign's zone from now on,
  // so it is written to Settings before anything is queued. The PUT is a whole
  // object, so it is built from freshly fetched settings; concurrent approves
  // share one in-flight sync. Send now hides the zone (and its note), so a
  // zone left over from a "later" pick is never written. Resolves true when it PUT.
  const syncCampaignZone = (): Promise<boolean> => {
    const zone = state.zone;
    if (state.when !== 'later' || !zone || !isZoneChange(zone, loadedCampaignZone)) return Promise.resolve(false);
    if (zoneSync.current?.zone === zone) return zoneSync.current.promise;
    const promise = settingsQuery.refetch({ throwOnError: true }).then(({ data: fresh }) => {
      if (!fresh) throw new Error('Campaign settings are not loaded');
      return isZoneChange(zone, fresh.sendTz) ? save.mutateAsync({ ...toSettingsInput(fresh), sendTz: zone }).then(() => true) : false;
    });
    const entry = { zone, promise };
    zoneSync.current = entry;
    const forget = () => {
      if (zoneSync.current === entry) zoneSync.current = null;
    };
    promise.then(forget, forget);
    return promise;
  };

  /** Saves a draft's edit (PATCH). Nothing is queued to send here — approval is the Send step's. */
  const saveDraft = async (draft: OutreachDraft, edit: DraftCardEdit) => {
    const id = draft.message.id;
    setSavingIds((prev) => [...prev, id]);
    setCardErrors((prev) => ({ ...prev, [id]: undefined }));
    try {
      await draftActions.patch.mutateAsync({ entryId: draft.entry.entryId, messageId: id, edit });
      if (!mounted.current) return;
      setEditedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } catch (e) {
      setCardErrors((prev) => ({ ...prev, [id]: draftSaveError(e) }));
    } finally {
      // Drops only THIS call's own entry, not every occurrence of the id.
      setSavingIds((prev) => {
        const at = prev.indexOf(id);
        return at === -1 ? prev : [...prev.slice(0, at), ...prev.slice(at + 1)];
      });
    }
  };

  // Go approves a first touch only under the step 3 batch; one that has expired, or never started, is redrafted first.
  const batchId = usableBatchId(run);
  // The batch is gone for good: Send stays off and step 3 offers Draft again.
  // Aged to the epoch rather than removed: its floor still tells this batch's drafts from older ones.
  const dropBatch = () => setRun(run ? { templateId: run.templateId, key: run.key, warnings: run.warnings, ...(run.batch ? { batch: { ...run.batch, startedAt: 0 } } : {}) } : null);

  const approve = async (ids: number[]) => {
    if (ids.length === 0 || approvingRef.current) return;
    if (state.when === 'later' && !isFutureSchedule(state, state.zone ?? campaignZone)) {
      setError(SCHEDULE_PAST_COPY);
      return;
    }
    const wizardBatchId = usableBatchId(run);
    if (!wizardBatchId) {
      setError(WIZARD_BATCH_EXPIRED_COPY);
      return;
    }
    approvingRef.current = true;
    setApproving(true);
    setError(null);
    const recipients = recipientsFor(ids);
    let zoneSaved = false;
    try {
      try {
        zoneSaved = await syncCampaignZone();
      } catch (e) {
        setError(zoneSaveError(e));
        return;
      }
      if (!mounted.current) return;
      const choices = sendChoices();
      const { operationId: id } = await draftActions.approveMany.mutateAsync({ messageIds: ids, ...choices, ...(state.when === 'now' && !choices.scheduledFor ? { sendNow: true } : {}), wizardBatchId });
      // Snapshotted only once the approve is accepted, so a failed attempt leaves nothing behind.
      setApprovedRecipients(recipients);
      onApproveOperationIdChange(id);
    } catch (e) {
      if (codeOf(e) === 'INVALID_WIZARD_BATCH_ID') dropBatch();
      setError(approveError(e, zoneSaved));
    } finally {
      approvingRef.current = false;
      setApproving(false);
    }
  };

  // Draft again (asked for) replaces every waiting draft; the old ones stay listed in each creator's draft menu. A reopen replaces nothing.
  const forgetBatch = () => {
    setPickedIds([]);
    setEditedIds([]);
    setCardErrors({});
    setBatchEntryIds(null);
  };

  /** Asks Go to draft every given creator from the batch's frozen template; the drafts arrive in the background.
   *  The operation it returns is the wizard batch the approve names; a single creator's Retry never replaces it. */
  const beginDrafting = async (templateId: number, entryIds: string[], base: WizardRun, replaceExisting = false) => {
    setStarting(true);
    setStartError(null);
    setError(null);
    onOperationIdChange(null);
    setRun({ templateId: base.templateId, key: base.key, warnings: base.warnings });
    try {
      const { operationId: id, draftFloor } = await startDrafts.mutateAsync({ entryIds, templateId, replaceExisting, includeNoEmail: true });
      setRun({ templateId: base.templateId, key: base.key, warnings: base.warnings, batch: { id, startedAt: Date.now(), floor: draftFloor } });
      onOperationIdChange(id);
    } catch (e) {
      if (mounted.current) setStartError(draftStartError(e));
    } finally {
      if (mounted.current) setStarting(false);
    }
  };

  const batchEntryIdsNow = () => sendableTargets(state).map((target) => target.entryId);
  const runTemplateId = run?.templateId ?? template?.id;

  /** A whole-batch draft again for the creators in this send, reusing the run's template. */
  const redraftAll = () => {
    if (runTemplateId === undefined) return;
    const entryIds = batchEntryIdsNow();
    forgetBatch();
    void beginDrafting(runTemplateId, entryIds, run ?? { templateId: runTemplateId, key: `${runTemplateId}|${entryIds.join(',')}` }, true);
  };

  const retryCreator = (entryId: string): Promise<{ operationId: string }> => {
    if (runTemplateId === undefined) return Promise.reject(new Error('No template for this batch'));
    return startDrafts.mutateAsync({ entryIds: [entryId], templateId: runTemplateId, replaceExisting: true, includeNoEmail: true, ...(run?.batch ? { batchId: run.batch.id } : {}) });
  };

  const templateCreator = (entryId: string): Promise<void> => draftActions.templateFirstTouch.mutateAsync({ entryId, templateId: runTemplateId });

  // The template is settled and saved to the Templates tab before a single draft is written from it. Going
  // Back and forward with nothing changed keeps the run, so a hand-edited draft is not regenerated by a Continue.
  const continueFromTemplate = async () => {
    if (!template || !state.templateSteps || committingRef.current) return;
    committingRef.current = true;
    setCommitting(true);
    setTemplateError(null);
    try {
      const committed = await commitBatchTemplate(template, state.templateSteps, { create: templateMutations.create.mutateAsync, patch: templateMutations.patch.mutateAsync });
      if (!mounted.current) return;
      const entryIds = batchEntryIdsNow();
      const key = `${committed.templateId}|${entryIds.join(',')}`;
      if (committed.templateId !== state.templateId) dispatch({ type: 'template', templateId: committed.templateId });
      if (committed.saved === 'unchanged' && run?.key === key && operationId && batchId) {
        dispatch({ type: 'next' });
        return;
      }
      forgetBatch();
      dispatch({ type: 'next' });
      void beginDrafting(committed.templateId, entryIds, { templateId: committed.templateId, key, warnings: committed.warnings });
    } catch (e) {
      if (mounted.current) setTemplateError(templateSaveError(e));
    } finally {
      committingRef.current = false;
      if (mounted.current) setCommitting(false);
    }
  };

  const onContinue = () => {
    if (state.step === 'template') {
      void continueFromTemplate();
      return;
    }
    if (state.step === 'drafts') setBatchEntryIds(readyEntryIds);
    dispatch({ type: 'next' });
  };

  // P32 (owner): Send approves at once, with no second pop-up.
  const requestApprove = () => void approve(sendIds);

  const onApproveDone = (operation: OutreachOperation) => {
    // The batch was refused as a whole: back to the Send step, where it can't be sent again until step 3 is redrafted.
    if (approveBatchExpired(operation)) {
      dropBatch();
      onApproveOperationIdChange(null);
      setError(WIZARD_BATCH_EXPIRED_COPY);
      return;
    }
    // Approved = a message is on its way; an applied outcome still waiting for an email is counted apart.
    const { sentIds, waitingEmailIds } = summarizeApprove(operation);
    const waiting = waitingEmailIds.length > 0 ? ` · ${waitingEmailIds.length} waiting for email` : '';
    onSummaryChange(`${sentIds.length} approved${waiting} · ${operation.skippedCount} skipped · ${failedTotal(operation)} failed`);
    toastSendFinished(operation, !!scheduledForISO(state, campaignZone));
    onQueued({ operationId: operation.id });
  };
  // The sending view has no progress bar, so the approve's end is watched here (once per operation).
  const doneFor = useRef<string | null>(null);
  const approveOperation = approveProgress.data;
  useEffect(() => {
    if (!approveOperation || approveOperation.status !== 'done' || summary !== null || doneFor.current === approveOperation.id) return;
    doneFor.current = approveOperation.id;
    onApproveDone(approveOperation);
    // onApproveDone only calls the parent's stable setters; the guard above keeps this to one call per operation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approveOperation, summary]);
  // Persist the wizard's own state so a remount resumes on the same step and schedule.
  useEffect(() => {
    session?.setSaved(state);
    // session is a fresh object each parent render; writing on every state change is the whole intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const draftsRunning = operationId !== null && approveOperationId === null && items.some((item) => item.state === 'writing');

  const sendable = sendableTargets(state).length;
  // Continue also waits out a mid-edit card — Drafts unmounting with an unsaved edit open would discard it silently.
  const continueDisabled = state.step === 'drafts'
    ? readyIds.length === 0 || messageEditingBlocked || hasIncompleteDraft || savingIds.length > 0
    : !canContinue(state, { hasTemplate: !!template }) || (state.step === 'template' && committing);
  const placeholderDraft = items.flatMap((item) => (item.state === 'ready' && inBatch(item.entryId) ? [{ username: item.username, token: draftPlaceholder(item.draft) }] : [])).find((draft) => draft.token);
  const scheduleTooFar = state.step === 'send' && state.when === 'later' && isBeyondScheduleCap(state, state.zone ?? campaignZone);
  const sendAlert = state.step === 'send' && !approveOperationId ? error ?? (sendIds.length > 0 && !batchId ? WIZARD_BATCH_EXPIRED_COPY : placeholderDraft?.token ? `@${placeholderDraft.username}: ${placeholderCopy(placeholderDraft.token)}` : scheduleTooFar ? SCHEDULE_TOO_FAR_COPY : null) : null;
  const sendBusy = approving || draftActions.approveMany.isPending;
  const sendDisabled = !canWrite || sendIds.length === 0 || sendBusy || !batchId || !!placeholderDraft || !canContinue(state, { hasTemplate: true, zone: state.zone ?? campaignZone }) || (state.when === 'later' && !loadedCampaignZone);
  const sendLabel = `${state.when === 'later' ? 'Schedule for' : 'Send to'} ${sendIds.length} ${sendIds.length === 1 ? 'Creator' : 'Creators'}`;
  // Only the approve locks the wizard (no operation-cancel route backs a Cancel); drafting never does (Ruling 37).
  const isRunningOperation = approveOperationId !== null && summary === null && !approveProgressError;
  // P40 (owner): a send running with no error past the wait can be left; closing cancels nothing server-side.
  const [slowApproveId, setSlowApproveId] = useState<string | null>(null);
  const stillSending = isRunningOperation && slowApproveId === approveOperationId;
  // An approve still waiting on its zone save or request, or a template being saved, must not outlive the wizard.
  const closeLocked = (isRunningOperation && !stillSending) || approving || committing;
  const currentIndex = WIZARD_STEPS.indexOf(state.step);
  // P32 (Figma 665:96219): from Send until the approve settles, one loader replaces the whole drawer.
  const resultView = approveOperationId && summary ? 'confirmation' : approveOperationId || approving ? 'sending' : null;

  const handleClose = () => {
    // Drafting never blocks the close (Ruling 37): the panel is put away and the session kept, so the Bulk sends list can bring it back.
    if (draftsRunning && onHide) {
      toast.info('Drafting continues in the background. Use Bulk sends in the header to come back to it.');
      onHide();
      return;
    }
    if (operationId && !approveOperationId && readyIds.length > 0) toast.info('Drafts are saved — open a creator in Not contacted to send.');
    const failedSaveCount = Object.values(cardErrors).filter(Boolean).length;
    if (failedSaveCount > 0) {
      toast.error(failedSaveCount === 1 ? '1 draft edit failed to save and was not applied.' : `${failedSaveCount} draft edits failed to save and were not applied.`);
    }
    // Leaving a slow send: the owner outlives this wizard, so it reports the end (Toast on finish).
    if (stillSending && approveOperationId && onLeaveSending) {
      onLeaveSending({ operationId: approveOperationId, scheduled: !!scheduledForISO(state, campaignZone) });
      toast.info("The send is still going. We'll tell you when it finishes.");
    }
    onClose();
  };

  useEffect(() => {
    if (!isRunningOperation || !approveOperationId) return;
    const timer = setTimeout(() => setSlowApproveId(approveOperationId), STILL_SENDING_AFTER_MS);
    return () => clearTimeout(timer);
  }, [isRunningOperation, approveOperationId]);
  // Before the approve is accepted the batch is the live one; after, the snapshot taken at Send.
  const sendingRecipients = resultView !== 'sending' ? {} : approveOperationId ? approvedRecipients : recipientsFor(sendIds);
  const sendingPeople: SummaryPerson[] = Object.entries(sendingRecipients).map(([id, r]) => ({ id: Number(id), ...r }));
  // Status honesty: a draft waiting for an email is approved but nothing goes out for it, so it is never in the sending count.
  const sendingWaiting = sendingPeople.filter((person) => person.waiting).length;
  const sendingReady = Math.max(0, (approveProgress.data?.total ?? sendingPeople.length) - sendingWaiting);
  const sendingScheduledFor = scheduledForISO(state, campaignZone);

  // The review content is replaced wholesale; move focus to what replaced it.
  // A failed approve swaps the drawer back to the send step: focus returns to Send, beside the error,
  // or to the error itself when Send is off (an expired batch).
  const sendRef = useRef<HTMLButtonElement>(null);
  const sendAlertRef = useRef<HTMLParagraphElement>(null);
  const sendAlertId = useId();
  const prevResultView = useRef(resultView);
  useEffect(() => {
    if (resultView) resultRef.current?.focus();
    else if (prevResultView.current) {
      const send = sendRef.current;
      (send && !send.disabled ? send : sendAlertRef.current)?.focus();
    }
    prevResultView.current = resultView;
  }, [resultView]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next || closeLocked) return;
        handleClose();
      }}
    >
      {/* sm:max-w-none: the base DialogContent caps at sm:max-w-lg, which
          tailwind-merge keeps unless overridden at the same breakpoint. */}
      <DialogContent
        className="top-0 right-0 left-auto flex h-[100dvh] w-full max-w-none translate-none flex-col gap-0 overflow-hidden rounded-none border-0 border-l p-0 sm:max-w-none lg:w-[min(1394px,calc(100vw-328px))]"
        // Figma's sending view and confirmation (665:96219, 680:64821) have no corner X.
        showCloseButton={!closeLocked && resultView === null}
        onEscapeKeyDown={closeLocked ? (e) => e.preventDefault() : undefined}
        onInteractOutside={closeLocked ? (e) => e.preventDefault() : undefined}
      >
        {resultView ? (
          // Keyed by view so the confirmation is a fresh region that takes focus and is announced anew.
          <div key={resultView} ref={resultRef} tabIndex={-1} role="region" aria-labelledby={RESULT_TITLE_ID} aria-describedby={RESULT_DESC_ID} className="flex min-h-0 flex-1 overflow-y-auto bg-[#F9F9F7] px-6 py-10 outline-none">
            <DialogTitle className="sr-only">Bulk send</DialogTitle>
            <DialogDescription className="sr-only">The progress and result of this send.</DialogDescription>
            {resultView === 'confirmation' && approveOperationId ? (
              <BulkSendConfirmation
                campaignId={campaignId}
                operationId={approveOperationId}
                scheduledFor={scheduledForISO(state, campaignZone)}
                zone={state.zone ?? campaignZone}
                held={heldEvents}
                recipients={approvedRecipients}
                suppressedDropped={suppressedDropped(state)}
                onClose={onClose}
                onNavigate={onNavigate}
              />
            ) : (
              <SendingView
                count={sendingReady}
                waiting={sendingWaiting}
                people={sendingPeople.filter((person) => !person.waiting)}
                scheduledAt={sendingScheduledFor ? scheduledTimeLabel(sendingScheduledFor, state.zone ?? campaignZone) : undefined}
                suppressed={suppressedDropped(state)}
                progressError={approveProgressError}
                stillSending={stillSending}
                onClose={approveProgressError || stillSending ? handleClose : undefined}
              />
            )}
          </div>
        ) : (
          <>
            <DialogHeader className="shrink-0 gap-1 border-b border-border px-6 py-3 pr-14 text-left">
              <DialogTitle className="text-2xl font-medium">Bulk send</DialogTitle>
              <DialogDescription className="sr-only">Choose creators and a template, review the drafts, and send.</DialogDescription>
              <p className="text-body-sm-regular text-muted-foreground">{`${sendable} ${sendable === 1 ? 'creator' : 'creators'}`}</p>
            </DialogHeader>
            <div className="flex min-h-0 flex-1">
              <div className="flex min-w-0 flex-1 flex-col bg-[#F9F9F7]">
                <ol aria-label="Steps" className="flex h-[49px] shrink-0 items-center gap-6 overflow-x-auto border-b border-border px-6 text-body-sm-medium">
                  {WIZARD_STEPS.map((step, index) => {
                    const complete = currentIndex > index;
                    const current = state.step === step;
                    return (
                      <Fragment key={step}>
                        <li aria-current={current ? 'step' : undefined} className={cn('flex shrink-0 items-center gap-2', current ? 'text-foreground' : 'text-muted-foreground')}>
                          <span className={cn('flex size-6 items-center justify-center rounded-full border text-xs', current && 'border-foreground bg-foreground text-background', complete && 'border-foreground text-foreground')}>
                            {complete ? <Check size={12} weight="bold" /> : index + 1}
                          </span>
                          <span className={cn(!current && 'hidden sm:inline')}>{stepLabels[step]}</span>
                        </li>
                        {index === currentIndex && index < WIZARD_STEPS.length - 1 ? <li aria-hidden="true" className="flex-1" /> : null}
                      </Fragment>
                    );
                  })}
                </ol>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                  {state.step === 'creators' ? <CreatorsStep state={state} dispatch={dispatch} /> : null}
                  {state.step === 'template' ? (
                    <TemplateStep
                      state={state}
                      dispatch={dispatch}
                      groups={templatesQuery.data}
                      loadError={!!templatesQuery.isError}
                      onFillStep={templateMutations.fillStep.mutateAsync}
                      error={templateError}
                    />
                  ) : null}
                  {state.step === 'drafts' ? (
                    <DraftsStep
                      state={state}
                      operationId={operationId}
                      onOperationIdChange={onOperationIdChange}
                      onRetryCreator={retryCreator}
                      renderDraftMenu={(draft) => <DraftMenu entryId={draft.entry.entryId} currentId={draft.message.id} campaignId={campaignId} canWrite={canWrite} onPicked={(id) => setPickedIds((prev) => [...prev, id])} />}
                      onTemplateCreator={templateCreator}
                      starting={starting}
                      templateWarnings={run?.warnings}
                      startError={startError}
                      onRetryStart={redraftAll}
                      batchExpired={operationId !== null && !starting && !startError && !batchId}
                      onRedraft={redraftAll}
                      canWrite={canWrite}
                      items={items}
                      loadError={batch.error}
                      onRetryLoad={batch.retry}
                      editedIds={editedIds}
                      savingIds={savingIds}
                      cardErrors={cardErrors}
                      onSaveDraft={(draft, edit) => void saveDraft(draft, edit)}
                      onDismissError={(id) => setCardErrors((prev) => ({ ...prev, [id]: undefined }))}
                      onEditingBlockChange={setMessageEditingBlocked}
                    />
                  ) : null}
                  {state.step === 'send' ? (
                    <div className="space-y-5">
                      <SendStep
                        summary={{ ready: sendIds.length - waitingIds.length, waiting: waitingIds.length }}
                        state={state}
                        dispatch={dispatch}
                        campaignZone={loadedCampaignZone}
                        adminZone={adminZone}
                        loadError={!!settingsQuery.isError}
                        retrying={settingsQuery.isFetching}
                        onRetryLoad={() => void settingsQuery.refetch()}
                      />
                      <div className="mx-auto w-full max-w-[880px] space-y-5">
                        {batch.error ? (
                          <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-body-sm-regular text-rose-800">
                            <span>Couldn&apos;t load the drafts. They&apos;re saved on each creator&apos;s row.</span>
                            <Button size="sm" variant="outline" onClick={batch.retry}>Retry</Button>
                          </div>
                        ) : null}
                        <div role="status">{liveBatchChange ? <p data-testid="send-batch-change" className="rounded-xl border border-border bg-card px-4 py-3 text-body-sm-medium text-foreground">{liveBatchChange}</p> : null}</div>
                        <RiskyDrafts items={batchItems} canSendAnyway={canOverrideAddressGuard} />
                      </div>
                    </div>
                  ) : null}
                  {error && state.step !== 'send' ? <p role="alert" className="mx-auto mt-4 max-w-[880px] text-body-sm-regular text-rose-700">{error}</p> : null}
                </div>
                {/* Outside the scrolling body so an approve failure shows next to the Send that caused it. */}
                {sendAlert ? <p ref={sendAlertRef} id={sendAlertId} role="alert" tabIndex={-1} className="shrink-0 border-t border-border bg-card px-6 py-2 text-body-sm-regular text-rose-700 outline-none">{sendAlert}</p> : null}
                <div className="flex h-[69px] shrink-0 items-center justify-between gap-3 border-t border-border bg-card px-6">
                  {state.step === 'creators'
                    ? <Button variant="ghost" className="h-11 rounded-full px-5" onClick={onClose}>Cancel</Button>
                    : <Button variant="ghost" className="h-11 rounded-full px-5" onClick={() => { setError(null); dispatch({ type: 'back' }); }} disabled={(state.step === 'drafts' && messageEditingBlocked) || (state.step === 'template' && committing) || (state.step === 'send' && sendBusy)}>Back</Button>}
                  {state.step === 'send'
                    ? <Button ref={sendRef} className="h-11 shrink-0 rounded-full px-5" onClick={requestApprove} disabled={sendDisabled} aria-describedby={sendAlert ? sendAlertId : undefined}>
                        {sendLabel}
                        <PaperPlaneTilt size={16} aria-hidden="true" />
                      </Button>
                    : <Button className="h-11 rounded-full px-5" onClick={onContinue} disabled={continueDisabled}>{state.step === 'template' && committing ? 'Saving…' : 'Continue'}</Button>}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
